import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Grid, TextField, Typography, Hidden, FormControl, InputLabel,
    MenuItem, Select
} from '@mui/material';
import { Search as IconSearch } from "@mui/icons-material";
import { setResults, setValue, setListCargo } from '../redux/trabajadorSearchSlice';
import { PaperComponentDraggable } from "./common/PaperComponentDraggable";
import * as trabajadorService from "../services/trabajadorService";
import * as cargoService from "../services/cargoService";
import * as filialService from "../services/filialService";
import DataGridBase from "./common/DataGridBase";

/**
 * Ventana estándar de búsqueda de personal (trabajadores) en el sistema.
 * Mismo formato y estilo que PersonSearch.
 */
const TrabajadorSearch = (props) => {
    const { open, idFilial } = props;
    const handleClose = props.openClose || props.onClose;
    const handleSelect = props.setSelection || props.onAceptar || props.onSelect;

    const filters = useSelector((state) => state.trabajadorSearch?.filters) || {
        nombres: "",
        idCargo: 0,
        idFilial: 0,
        idEstado: 2,
    };
    const listCargo = useSelector((state) => state.trabajadorSearch?.listCargo) || [];
    const results = useSelector((state) => state.trabajadorSearch?.results) || [];
    const dispatch = useDispatch();

    const [listFilial, setListFilial] = useState([]);
    const [filaElegida, setFilaElegida] = useState(null);
    const [cargando, setCargando] = useState(false);
    const dialogRef = useRef();
    const inputRef = useRef(null);

    // Cargar listas de apoyo para filtros
    useEffect(() => {
        if (!open) return;
        setFilaElegida(null);

        if (idFilial !== undefined && idFilial > 0) {
            dispatch(setValue({ name: "idFilial", value: idFilial }));
        }

        // Cargar filiales
        filialService.getFilialesList()
            .then((data) => setListFilial(data || []))
            .catch((err) => console.error("Error al cargar filiales:", err));

        // Cargar cargos activos
        cargoService.getsCargoViewSearchBasic("", 2)
            .then((data) => dispatch(setListCargo(data || [])))
            .catch((err) => console.error("Error al cargar cargos:", err));

        setTimeout(() => inputRef.current?.focus(), 250);
    }, [open, idFilial, dispatch]);

    const fetchResults = useCallback(async () => {
        setCargando(true);
        try {
            const data = await trabajadorService.getsTrabajadorResumenView({
                nombres: filters.nombres ?? "",
                idCargo: filters.idCargo ?? 0,
                idFilial: filters.idFilial ?? 0,
                idEstado: filters.idEstado ?? 2,
            });
            const lista = Array.isArray(data)
                ? data
                : (data?.data?.data || data?.data || []);

            // Asegurar que cada fila tenga un identificador único para DataGrid
            const listaConId = lista.map((item) => ({
                ...item,
                id: item.idTrabajador || item.id || item.idPersona,
            }));

            dispatch(setResults(listaConId));
        } catch (err) {
            console.error("Error al buscar trabajadores:", err);
            dispatch(setResults([]));
        } finally {
            setFilaElegida(null);
            setCargando(false);
        }
    }, [dispatch, filters.nombres, filters.idCargo, filters.idFilial, filters.idEstado]);

    // Búsqueda inicial automática al abrir
    useEffect(() => {
        if (open) {
            fetchResults();
        }
    }, [open, fetchResults]);

    const seleccionar = (fila) => {
        if (!fila) return;
        if (handleSelect) handleSelect(fila);
        if (handleClose) handleClose();
    };

    const columns = [
        { field: 'idTrabajador', headerName: 'ID', width: 65, sortable: true, headerAlign: 'center', align: 'center' },
        { field: 'nombres', headerName: 'Nombres', width: 230, sortable: true },
        { field: 'tipoDocumento', headerName: 'Tip.Doc.', width: 85, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'numeroDocumento', headerName: 'Num.Doc.', width: 105, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'cargo', headerName: 'Cargo', width: 140, sortable: true },
        { field: 'filial', headerName: 'Filial', width: 110, sortable: true },
        { field: 'estado', headerName: 'Estado', width: 90, sortable: false, headerAlign: 'center', align: 'center' },
    ];

    return (
        <Dialog
            open={!!open}
            onClose={handleClose}
            fullWidth={true}
            maxWidth={"md"}
            ref={dialogRef}
            PaperComponent={PaperComponentDraggable}
        >
            <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Buscar Trabajador
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ minHeight: "350px", maxHeight: "70vh", overflowY: "auto" }}>
                <Grid container item xs={12} sm={12} spacing={1} padding={1} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            name="nombres"
                            label="Nombres o Doc."
                            placeholder="Buscar por nombre..."
                            value={filters.nombres || ""}
                            fullWidth
                            onChange={(event) => {
                                dispatch(setValue({ name: "nombres", value: event.target.value }));
                            }}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    fetchResults();
                                }
                            }}
                            size="small"
                            inputRef={inputRef}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Cargo</InputLabel>
                            <Select
                                label="Cargo"
                                value={filters.idCargo ?? 0}
                                onChange={(event) => {
                                    dispatch(setValue({ name: "idCargo", value: event.target.value }));
                                }}
                            >
                                <MenuItem value={0}>(Todos)</MenuItem>
                                {listCargo.map((c) => (
                                    <MenuItem key={c.idCargo || c.id} value={c.idCargo || c.id}>
                                        {c.descripcion || c.cargo}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Filial</InputLabel>
                            <Select
                                label="Filial"
                                value={filters.idFilial ?? 0}
                                onChange={(event) => {
                                    dispatch(setValue({ name: "idFilial", value: event.target.value }));
                                }}
                            >
                                <MenuItem value={0}>(Todas)</MenuItem>
                                {listFilial.map((f) => (
                                    <MenuItem key={f.id || f.idFilial} value={f.id || f.idFilial}>
                                        {f.descripcion || f.nombre}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={2} display="flex" alignItems="center">
                        <Button variant="outlined" fullWidth onClick={fetchResults}>
                            {<Hidden smDown>Buscar</Hidden>}
                            <IconSearch />
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <DataGridBase
                            rows={results}
                            columns={columns}
                            loading={cargando}
                            paginationTop={false}
                            pageSize={5}
                            pageSizeOptions={[5, 10, 20, 50]}
                            disableRowSelectionOnClick={false}
                            onRowClick={(params) => setFilaElegida(params.row)}
                            onRowDoubleClick={(params) => seleccionar(params.row)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    color="primary"
                    variant="contained"
                    disabled={!filaElegida}
                    onClick={() => seleccionar(filaElegida)}
                >
                    Aceptar
                </Button>
                <Button variant="outlined" onClick={handleClose}>
                    Cancelar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TrabajadorSearch;

